import api from '../services/api';
import producerService from '../services/producerService';
import grapeService from '../services/grapeService';
import wineRegionService from '../services/wineRegionService';
import wineNameService from '../services/wineNameService';
import wineService from '../services/wineService';
import { parseVintageInput } from '../services/wineService';
import { countryCodeToName, getCountryName } from './countryUtils';
import type { ColumnDef } from './excelUtils';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ExportFilterDef {
  key: string;
  label: string;
  type: 'text' | 'select';
  options?: Array<{ value: string; label: string }>;
}

export interface ImportRowResult {
  /**
   * 'created'  — record was created successfully.
   * 'skipped'  — intentional skip (duplicate detected); not an error.
   * 'error'    — validation failure or unresolvable dependency; shown in error list.
   */
  action: 'created' | 'skipped' | 'error';
  displayName: string;
  /** Human-readable explanation. Required for 'skipped' and 'error', absent for 'created'. */
  reason?: string;
  /** Which column or step caused the problem. Included when determinable. */
  field?: string;
}

/** Unified import result used by both row-by-row and batch import paths. */
export interface ImportSummary {
  created: number;
  /** Regions that already existed and were updated in-place (batch mode only). */
  updated: number;
  /** Intentional skips only — duplicates. Does NOT include rows that failed with errors. */
  skipped: number;
  /** Minimal parent records auto-created because the parent was not in the file or DB. */
  autoCreatedParents: number;
  /** Human-readable messages about circular or conflicting parent definitions. */
  hierarchyConflicts: string[];
  /** Parent names that could not be resolved and were left unlinked. */
  unresolvedParents: string[];
  /**
   * Region names that exist in DB and were matched, but whose parent could not
   * be changed despite a valid parentId being sent in the PUT request.
   * These regions were updated (name/country) but their hierarchy position was
   * NOT corrected (e.g. circular hierarchy, invalid parent, or unexpected error).
   */
  reparentingFailed: string[];
  errors: Array<{ row: number; displayName: string; field?: string; reason: string }>;
}

export interface ImportExportConfig {
  moduleName: string;
  /** Columns used for the import template (.xlsx download + row parsing) */
  columns: ColumnDef[];
  /**
   * Optional separate column set for export downloads.
   * Use this when the export should include read-only/derived fields
   * (e.g. hierarchyPath) that must NOT appear in the import template.
   * Falls back to `columns` when not provided.
   */
  exportColumns?: ColumnDef[];
  exportFilters: ExportFilterDef[];
  importRow: (row: Record<string, string>, cache: ImportCache) => Promise<ImportRowResult>;
  /**
   * Optional multi-pass batch import that replaces the row-by-row loop.
   * Use for hierarchical data where parent/child ordering in the file is arbitrary.
   * When present, the modal calls this instead of iterating `importRow`.
   */
  importBatch?: (
    rows: Record<string, string>[],
    onProgress?: (current: number, total: number) => void,
  ) => Promise<ImportSummary>;
  fetchForExport: (filters: Record<string, string>) => Promise<Record<string, string>[]>;
}

// ── Country lookup helpers ────────────────────────────────────────────────────

const countryNameToCode: Record<string, string> = Object.fromEntries(
  Object.entries(countryCodeToName).map(([code, name]) => [name.toLowerCase(), code])
);

function resolveCountryCode(input: string): string {
  if (!input) return 'UNKNOWN';
  const upper = input.trim().toUpperCase();
  if (countryCodeToName[upper]) return upper; // already a code
  return countryNameToCode[input.trim().toLowerCase()] ?? 'UNKNOWN';
}

const countrySelectOptions = Object.entries(countryCodeToName)
  .sort(([, a], [, b]) => a.localeCompare(b))
  .map(([code, name]) => ({ value: code, label: name }));

// ── ImportCache ───────────────────────────────────────────────────────────────
// Caches entity _ids by name across an import run to avoid redundant API calls.

export class ImportCache {
  private producers = new Map<string, string>();
  private regions = new Map<string, string>();
  private grapes = new Map<string, string>();

  async findOrCreateProducer(name: string): Promise<string> {
    const key = name.trim().toLowerCase();
    if (this.producers.has(key)) return this.producers.get(key)!;

    // pageSize 200: backend text-search is ranked, a small page can miss an exact match.
    const searchProducer = async () => {
      const resp = await producerService.getAllProducers(1, 200, 'name', 'asc', name.trim());
      return resp.data.find(
        (p) => p.name.toLowerCase() === key || (p.fullName || '').toLowerCase() === key,
      ) ?? null;
    };

    const found = await searchProducer();
    if (found) {
      this.producers.set(key, found._id);
      return found._id;
    }

    try {
      const created = await producerService.createProducer({
        name: name.trim(),
        fullName: name.trim(),
        country: 'UNKNOWN',
        needsReview: true,
      } as any);
      this.producers.set(key, created._id);
      return created._id;
    } catch (createErr: any) {
      // Retry search in case a concurrent import or a search-rank miss caused
      // the create to fail with a uniqueness conflict.
      const retry = await searchProducer();
      if (retry) {
        this.producers.set(key, retry._id);
        return retry._id;
      }
      throw createErr;
    }
  }

  async findOrCreateRegion(name: string): Promise<string> {
    const key = name.trim().toLowerCase();
    if (this.regions.has(key)) return this.regions.get(key)!;

    // pageSize 200: search endpoint ranks by relevance; small pages miss exact matches.
    const searchRegion = async () => {
      const resp = await wineRegionService.searchWineRegions(name.trim(), 1, 200);
      return resp.data.find((r) => r.name.toLowerCase() === key) ?? null;
    };

    const found = await searchRegion();
    if (found) {
      this.regions.set(key, found._id);
      return found._id;
    }

    try {
      const created = await wineRegionService.createWineRegion({
        name: name.trim(),
        needsReview: true,
      } as any);
      this.regions.set(key, created._id);
      return created._id;
    } catch (createErr: any) {
      const retry = await searchRegion();
      if (retry) {
        this.regions.set(key, retry._id);
        return retry._id;
      }
      throw createErr;
    }
  }

  /**
   * @param inferredColor  Grape color inferred from the wine being imported.
   *   Passed when available so auto-created stubs are not all wrongly tagged RED.
   *   Falls back to 'RED' when undefined (Sparkling / Dessert / Fortified are ambiguous).
   */
  async findOrCreateGrape(
    name: string,
    inferredColor?: 'RED' | 'WHITE' | 'PINK_ROSE',
  ): Promise<string> {
    const key = name.trim().toLowerCase();
    if (this.grapes.has(key)) return this.grapes.get(key)!;

    // pageSize 200: backend text-search ranks by relevance across primaryName AND
    // aliases, so a small page can easily miss an exact primaryName match.
    const searchGrape = async () => {
      const resp = await api.get('/admin/grape', {
        params: { q: name.trim(), pageSize: 200 },
      });
      const data: any[] = resp.data?.data ?? [];
      return data.find((g: any) => g.primaryName.toLowerCase() === key) ?? null;
    };

    const found = await searchGrape();
    if (found) {
      this.grapes.set(key, found._id);
      return found._id;
    }

    try {
      const created = await grapeService.createGrape({
        primaryName: name.trim(),
        aliases: [],
        color: inferredColor ?? 'RED',
        needsReview: true,
      } as any);
      this.grapes.set(key, created._id);
      return created._id;
    } catch (createErr: any) {
      // If creation failed because the grape already exists (search missed it despite
      // pageSize 200 — e.g. > 200 alias matches), retry search before propagating.
      const retry = await searchGrape();
      if (retry) {
        this.grapes.set(key, retry._id);
        return retry._id;
      }
      throw createErr;
    }
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Fetch all pages of a paginated API, returns flat data array */
async function fetchAllPages<T>(
  fetchPage: (page: number, pageSize: number) => Promise<{ data: T[]; pagination: any }>
): Promise<T[]> {
  const pageSize = 500;
  const first = await fetchPage(1, pageSize);
  const all: T[] = [...first.data];
  for (let p = 2; p <= first.pagination.totalPages; p++) {
    const next = await fetchPage(p, pageSize);
    all.push(...next.data);
  }
  return all;
}

// ── Producer Config ───────────────────────────────────────────────────────────

export const producerConfig: ImportExportConfig = {
  moduleName: 'Producers',
  columns: [
    { header: 'Name', example: 'Gaja' },
    { header: 'Full Name', example: 'Gaja' },
    { header: 'Country', example: 'Italy' },
    { header: 'Region', example: 'Piedmont' },
  ],
  exportFilters: [
    { key: 'country', label: 'Country', type: 'select', options: countrySelectOptions },
  ],

  importRow: async (row) => {
    const name = row['Name']?.trim();
    if (!name) return { action: 'error', displayName: '(empty row)', reason: 'Name is empty', field: 'Name' };

    const nameLower = name.toLowerCase();

    // Duplicate check — pageSize 200 so a ranked text-search can't miss an existing
    // exact match; also checks fullName to catch producers stored with different primary name.
    const resp = await producerService.getAllProducers(1, 200, 'name', 'asc', name);
    const dup = resp.data.find(
      (p) =>
        p.name.toLowerCase() === nameLower ||
        (p.fullName || '').toLowerCase() === nameLower,
    );
    if (dup) return { action: 'skipped', displayName: name, reason: 'Duplicate' };

    await producerService.createProducer({
      name,
      fullName: row['Full Name']?.trim() || name,
      country: resolveCountryCode(row['Country']),
      region: row['Region']?.trim() || undefined,
    } as any);

    return { action: 'created', displayName: name };
  },

  fetchForExport: async (filters) => {
    const countries = filters.country ? [filters.country] : undefined;
    const all = await fetchAllPages((p, ps) =>
      producerService.getAllProducers(p, ps, 'name', 'asc', undefined, countries)
    );
    return all.map((p) => ({
      Name: p.name,
      'Full Name': p.fullName,
      Country: getCountryName(p.country),
      Region: (p as any).region || '',
    }));
  },
};

// ── Grape Config ──────────────────────────────────────────────────────────────

const grapeColorLabel: Record<string, string> = {
  RED: 'Red',
  WHITE: 'White',
  PINK_ROSE: 'Rosé',
};

const grapeColorFromLabel: Record<string, 'RED' | 'WHITE' | 'PINK_ROSE'> = {
  red: 'RED',
  white: 'WHITE',
  rosé: 'PINK_ROSE',
  rose: 'PINK_ROSE',
  pink: 'PINK_ROSE',
};

export const grapeConfig: ImportExportConfig = {
  moduleName: 'Grapes',
  columns: [
    { header: 'Primary Name', example: 'Nebbiolo' },
    { header: 'Color', example: 'Red' },
    { header: 'Aliases', example: 'Spanna,Chiavennasca' },
    { header: 'Species', example: 'Vitis vinifera' },
  ],
  exportFilters: [
    {
      key: 'color',
      label: 'Color',
      type: 'select',
      options: [
        { value: '', label: 'All colors' },
        { value: 'RED', label: 'Red' },
        { value: 'WHITE', label: 'White' },
        { value: 'PINK_ROSE', label: 'Rosé' },
      ],
    },
  ],

  importRow: async (row) => {
    const primaryName = row['Primary Name']?.trim();
    if (!primaryName) return { action: 'error', displayName: '(empty row)', reason: 'Primary Name is empty', field: 'Primary Name' };

    const primaryNameLower = primaryName.toLowerCase();

    // Duplicate check — pageSize 200: backend text-search also matches aliases, so a
    // small page can return alias-matching grapes before the primary-name match.
    const searchGrapeByName = async () => {
      const resp = await api.get('/admin/grape', { params: { q: primaryName, pageSize: 200 } });
      const data: any[] = resp.data?.data ?? [];
      return data.find((g: any) => g.primaryName.toLowerCase() === primaryNameLower) ?? null;
    };

    const existing = await searchGrapeByName();
    if (existing) return { action: 'skipped', displayName: primaryName, reason: 'Duplicate' };

    const colorInput = (row['Color'] || '').trim().toLowerCase();
    const color = grapeColorFromLabel[colorInput] ?? 'RED';

    const aliasNames = (row['Aliases'] || '')
      .split(',')
      .map((a) => a.trim())
      .filter(Boolean);

    try {
      await grapeService.createGrape({
        primaryName,
        color,
        aliases: aliasNames.map((name) => ({ name, isOfficial: false })),
        species: row['Species']?.trim() || undefined,
      } as any);
    } catch (createErr: any) {
      // If the backend rejects with a conflict (the grape exists but our search missed it),
      // do one retry search before treating it as a real error.
      const retryFound = await searchGrapeByName();
      if (retryFound) return { action: 'skipped', displayName: primaryName, reason: 'Duplicate' };
      throw createErr;
    }

    return { action: 'created', displayName: primaryName };
  },

  fetchForExport: async (filters) => {
    const all = await fetchAllPages((p, ps) => grapeService.getAllGrapes(p, ps));
    const colorFilter = filters.color;
    const filtered = colorFilter ? all.filter((g) => g.color === colorFilter) : all;
    return filtered.map((g) => ({
      'Primary Name': g.primaryName,
      Color: grapeColorLabel[g.color] ?? g.color,
      Aliases: g.aliases.map((a) => a.name).join(','),
      Species: g.species || '',
    }));
  },
};

// ── WineRegion Config ─────────────────────────────────────────────────────────

export const regionConfig: ImportExportConfig = {
  moduleName: 'Wine Regions',
  // Import template: only the fields a human should fill in
  columns: [
    { header: 'Name', example: 'Barbaresco' },
    { header: 'Country', example: 'Italy' },
    { header: 'Parent Region', example: 'Piedmont' },
  ],
  // Export: also includes the derived hierarchyPath (read-only, auto-generated by backend)
  exportColumns: [
    { header: 'Name', example: 'Barbaresco' },
    { header: 'Country', example: 'Italy' },
    { header: 'Parent Region', example: 'Piedmont' },
    { header: 'Hierarchy Path', example: 'Italy > Piedmont > Barbaresco' },
  ],
  exportFilters: [
    { key: 'country', label: 'Country', type: 'select', options: countrySelectOptions },
  ],

  // importRow is kept for API compatibility but importBatch takes precedence in the modal.
  importRow: async (row, cache) => {
    const name = row['Name']?.trim();
    if (!name) return { action: 'error', displayName: '(empty row)', reason: 'Name is empty', field: 'Name' };

    const resp = await wineRegionService.searchWineRegions(name, 1, 200);
    const dup = resp.data.find((r) => r.name.toLowerCase() === name.toLowerCase());
    if (dup) return { action: 'skipped', displayName: name, reason: 'Duplicate' };

    const parentName = row['Parent Region']?.trim();
    let parentId: string | undefined;
    if (parentName) {
      parentId = await cache.findOrCreateRegion(parentName);
    }

    await wineRegionService.createWineRegion({
      name,
      country: resolveCountryCode(row['Country']),
      parentId,
    } as any);

    return { action: 'created', displayName: name };
  },

  /**
   * Multi-pass hierarchical import.
   *
   * Pass 1 — Normalize & deduplicate rows within the file.
   * Pass 2 — DB lookup: find existing _ids for all batch names.
   * Pass 3 — Topological sort of in-batch parent→child edges.
   * Pass 4 — Process in topo order: resolve parentId THEN create/update in one
   *           call. Because parents are processed before children, their _ids
   *           are in idMap by the time any child is created, so parentId is
   *           included in the CREATE payload.
   *           For existing regions, parentId is sent in the PUT body — the backend
   *           validates it (no circular refs, no self-parenting) and cascades
   *           hierarchy recalculation to all descendants.
   */
  importBatch: async (rows, onProgress) => {
    const summary: ImportSummary = {
      created: 0,
      updated: 0,
      skipped: 0,
      autoCreatedParents: 0,
      hierarchyConflicts: [],
      unresolvedParents: [],
      reparentingFailed: [],
      errors: [],
    };

    // ── Pre-pass: load all country nodes (code → _id) ─────────────────────────
    // Countries are stored with their 2-letter ISO code as name (e.g. "AU").
    // We use getCountries (not search) for reliable exact-match lookup.
    const countryIdMap = new Map<string, string>(); // "AU" → _id
    try {
      const countryResp = await fetchAllPages((p, ps) =>
        wineRegionService.getCountries(p, ps)
      );
      for (const c of countryResp) {
        countryIdMap.set(c.name.toUpperCase(), c._id);
      }
      console.log(`[Import pre] loaded ${countryIdMap.size} country nodes`);
    } catch (e) {
      console.warn('[Import pre] failed to load country nodes', e);
    }

    // ── Pass 1: Normalize & deduplicate ───────────────────────────────────────
    const batchMap = new Map<string, { row: Record<string, string>; rowIndex: number }>();

    for (let i = 0; i < rows.length; i++) {
      const name = rows[i]['Name']?.trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (batchMap.has(key)) {
        summary.skipped++;
        summary.hierarchyConflicts.push(
          `"${name}" appears more than once (rows ${batchMap.get(key)!.rowIndex + 3} and ${i + 3}); first used`
        );
        continue;
      }
      batchMap.set(key, { row: rows[i], rowIndex: i });
    }

    // ── Pass 2: DB lookup — find existing _ids and current parentIds ─────────
    // idMap grows throughout Pass 4 as new regions are created.
    const idMap = new Map<string, string>();
    // existingParentIds: the parentId each DB-matched region currently has.
    // Used in Pass 4 to detect whether re-parenting is needed and whether it succeeded.
    const existingParentIds = new Map<string, string | undefined>();

    await Promise.all(
      Array.from(batchMap).map(async ([key, { row }]) => {
        const name = row['Name'].trim();
        try {
          // pageSize 200: region search is ranked; small pages miss exact matches.
          const resp = await wineRegionService.searchWineRegions(name, 1, 200);
          const found = resp.data.find((r) => r.name.toLowerCase() === key);
          if (found) {
            idMap.set(key, found._id);
            existingParentIds.set(key, (found as any).parentId ?? undefined);
            console.log(
              `[Import P2] matched   "${name}" → ${found._id}` +
              ` existingParentId=${(found as any).parentId ?? 'none'}`
            );
          }
        } catch { /* will attempt create in Pass 4 */ }
      })
    );

    console.log(`[Import P1] batchMap size after dedup: ${batchMap.size} (raw rows: ${rows.length})`);

    // ── Pass 3: Topological sort ──────────────────────────────────────────────
    // Only in-batch parent→child edges are relevant for ordering.
    const childrenOf = new Map<string, string[]>();
    const inDegree = new Map<string, number>();

    for (const key of Array.from(batchMap.keys())) {
      childrenOf.set(key, []);
      inDegree.set(key, 0);
    }

    for (const [key, { row }] of Array.from(batchMap)) {
      const pk = row['Parent Region']?.trim().toLowerCase();
      if (pk && batchMap.has(pk)) {
        inDegree.set(key, (inDegree.get(key) ?? 0) + 1);
        childrenOf.get(pk)!.push(key);
      }
    }

    // Kahn's algorithm — produce level groups so each level can be processed in parallel.
    // Nodes at the same level have no parent-child relationship with each other.
    const topoLevels: string[][] = [];
    let currentWave = Array.from(inDegree.entries())
      .filter(([, deg]) => deg === 0)
      .map(([key]) => key);
    while (currentWave.length > 0) {
      topoLevels.push(currentWave);
      const nextWave: string[] = [];
      for (const key of currentWave) {
        for (const child of childrenOf.get(key) ?? []) {
          const d = (inDegree.get(child) ?? 1) - 1;
          inDegree.set(child, d);
          if (d === 0) nextWave.push(child);
        }
      }
      currentWave = nextWave;
    }

    const failedKeys = new Set<string>();
    for (const [key, deg] of Array.from(inDegree)) {
      if (deg > 0) {
        const name = batchMap.get(key)!.row['Name'].trim();
        summary.hierarchyConflicts.push(`"${name}" is part of a circular dependency — skipped`);
        failedKeys.add(key);
      }
    }

    const topoTotal = topoLevels.reduce((sum, l) => sum + l.length, 0);
    console.log(`[Import P3] ${topoLevels.length} levels, ${topoTotal} regions, ${failedKeys.size} cycles`);

    // ── Pass 4: Level-by-level parallel processing ────────────────────────────
    // All regions at the same level are independent — process them simultaneously.
    // Parents are always in an earlier level, so their _ids are in idMap before
    // any child level starts.

    const externalCache = new Map<string, { id: string; source: 'db' | 'auto-created' }>();

    async function resolveExternalParent(
      parentName: string
    ): Promise<{ id: string; source: 'db' | 'auto-created' } | undefined> {
      const pk = parentName.toLowerCase();
      if (externalCache.has(pk)) return externalCache.get(pk)!;

      // pageSize 200: ranked search; small pages miss exact matches.
      const searchParent = async () => {
        const resp = await wineRegionService.searchWineRegions(parentName, 1, 200);
        return resp.data.find((r) => r.name.toLowerCase() === pk) ?? null;
      };

      try {
        const found = await searchParent();
        if (found) {
          const entry = { id: found._id, source: 'db' as const };
          externalCache.set(pk, entry);
          return entry;
        }
      } catch { /* fall through to create */ }

      try {
        const created = await wineRegionService.createWineRegion({ name: parentName, needsReview: true } as any);
        const entry = { id: created._id, source: 'auto-created' as const };
        externalCache.set(pk, entry);
        summary.autoCreatedParents++;
        return entry;
      } catch {
        // Create failed — may be a conflict if search missed an existing record.
        // One more search attempt before giving up.
        try {
          const retry = await searchParent();
          if (retry) {
            const entry = { id: retry._id, source: 'db' as const };
            externalCache.set(pk, entry);
            return entry;
          }
        } catch { /* give up */ }
        summary.unresolvedParents.push(parentName);
        return undefined;
      }
    }

    // Pre-fetch all external parents in parallel before Pass 4 to avoid
    // sequential lookups inside the level loops.
    const externalParentNames = new Set<string>();
    for (const level of topoLevels) {
      for (const key of level) {
        if (failedKeys.has(key)) continue;
        const { row } = batchMap.get(key)!;
        const parentName = row['Parent Region']?.trim();
        if (!parentName) continue;
        const parentKey = parentName.toLowerCase();
        const country = resolveCountryCode(row['Country']);
        const rowCountryName = (countryCodeToName[country] ?? '').toLowerCase();
        const parentIsCountryName = rowCountryName !== '' && parentKey === rowCountryName;
        if (!parentIsCountryName && !batchMap.has(parentKey)) {
          externalParentNames.add(parentName);
        }
      }
    }
    await Promise.all(Array.from(externalParentNames).map((pName) => resolveExternalParent(pName)));
    console.log(`[Import P4] pre-fetched ${externalParentNames.size} external parents`);

    let processedCount = 0;

    for (const level of topoLevels) {
      await Promise.all(level.map(async (key) => {
        onProgress?.(++processedCount, topoTotal);
        if (failedKeys.has(key)) return;

        const { row, rowIndex } = batchMap.get(key)!;
        const name = row['Name'].trim();
        const country = resolveCountryCode(row['Country']);
        const parentName = row['Parent Region']?.trim();
        const parentKey = parentName?.toLowerCase() ?? '';

        // ── Resolve parentId ────────────────────────────────────────────────
        const rowCountryName = (countryCodeToName[country] ?? '').toLowerCase();
        const parentIsCountryName = rowCountryName !== '' && parentKey === rowCountryName;

        let resolvedParentId: string | undefined;

        if (parentName) {
          if (parentIsCountryName && !batchMap.has(parentKey)) {
            resolvedParentId = countryIdMap.get(country.toUpperCase());
          } else if (idMap.has(parentKey)) {
            resolvedParentId = idMap.get(parentKey);
          } else if (batchMap.has(parentKey)) {
            // Parent was in batch but failed — propagate.
            summary.errors.push({
              row: rowIndex + 3,
              displayName: name,
              reason: `Parent "${parentName}" failed to import — this region skipped`,
            });
            failedKeys.add(key);
            return;
          } else {
            // External parent (already in externalCache from pre-fetch).
            const resolved = externalCache.get(parentKey);
            if (resolved) {
              resolvedParentId = resolved.id;
            } else {
              summary.errors.push({
                row: rowIndex + 3,
                displayName: name,
                reason: `Parent "${parentName}" could not be resolved — region skipped`,
              });
              failedKeys.add(key);
              return;
            }
          }
        }

        const payload: Record<string, unknown> = { name, country };
        if (resolvedParentId) payload['parentId'] = resolvedParentId;

        try {
          if (idMap.has(key)) {
            const regionId = idMap.get(key)!;
            await wineRegionService.updateWineRegion(regionId, payload as any);
            summary.updated++;
          } else {
            const created = await wineRegionService.createWineRegion(payload as any);
            idMap.set(key, created._id);
            summary.created++;
          }
        } catch (err: any) {
          const errMsg = err?.response?.data?.message ?? err?.message ?? 'Unknown error';
          const wasUpdate = idMap.has(key);

          if (wasUpdate && resolvedParentId) {
            summary.reparentingFailed.push(name);
            summary.hierarchyConflicts.push(`"${name}" could not be re-parented: ${errMsg}`);
            summary.updated++;
          } else if (!wasUpdate) {
            // Create failed — try to recover existing record so children can still link.
            let recovered = false;
            try {
              const resp = await wineRegionService.searchWineRegions(name, 1, 200);
              const found = resp.data.find((r) => r.name.toLowerCase() === key);
              if (found) {
                idMap.set(key, found._id);
                summary.updated++;
                recovered = true;
              }
            } catch { /* recovery failed */ }
            if (!recovered) {
              summary.errors.push({ row: rowIndex + 3, displayName: name, reason: errMsg });
              failedKeys.add(key);
            }
          }
        }
      }));
    }

    return summary;
  },

  fetchForExport: async (filters) => {
    const all = await fetchAllPages((p, ps) =>
      wineRegionService.getAllRegions(p, ps)
    );

    const countryFilter = filters.country;
    const filtered = countryFilter
      ? all.filter((r) => (r.country || r.name) === countryFilter)
      : all;

    return filtered.map((r) => ({
      Name: r.name,
      Country: r.country ? getCountryName(r.country) : '',
      'Parent Region': r.hierarchyNames && r.hierarchyNames.length > 1
        ? r.hierarchyNames[r.hierarchyNames.length - 2]
        : '',
      'Hierarchy Path': r.hierarchyNames ? r.hierarchyNames.join(' > ') : r.name,
    }));
  },
};

// ── WineName lookup maps ──────────────────────────────────────────────────────

const wineColorFromLabel: Record<string, 'RED' | 'WHITE' | 'ROSE' | 'ORANGE' | 'SPARKLING' | 'DESSERT' | 'FORTIFIED'> = {
  red: 'RED', white: 'WHITE', rosé: 'ROSE', rose: 'ROSE',
  orange: 'ORANGE', sparkling: 'SPARKLING', dessert: 'DESSERT', fortified: 'FORTIFIED',
};

const wineColorLabel: Record<string, string> = {
  RED: 'Red', WHITE: 'White', ROSE: 'Rosé', ORANGE: 'Orange',
  SPARKLING: 'Sparkling', DESSERT: 'Dessert', FORTIFIED: 'Fortified',
};

const wineTypeFromLabel: Record<string, 'WINE' | 'SPIRITS' | 'BEER' | 'OTHER'> = {
  wine: 'WINE', spirits: 'SPIRITS', spirit: 'SPIRITS', beer: 'BEER', other: 'OTHER',
};

const wineTypeLabel: Record<string, string> = {
  WINE: 'Wine', SPIRITS: 'Spirits', BEER: 'Beer', OTHER: 'Other',
};

const wineSubTypeFromLabel: Record<string, 'STILL' | 'SPARKLING' | 'FORTIFIED' | 'DESSERT' | 'SWEET' | 'NATURAL'> = {
  still: 'STILL', sparkling: 'SPARKLING', fortified: 'FORTIFIED',
  dessert: 'DESSERT', sweet: 'SWEET', natural: 'NATURAL',
};

const wineSubTypeLabel: Record<string, string> = {
  STILL: 'Still', SPARKLING: 'Sparkling', FORTIFIED: 'Fortified',
  DESSERT: 'Dessert', SWEET: 'Sweet', NATURAL: 'Natural',
};

const vintageConfigFromLabel: Record<string, 'SEQUENTIAL' | 'NON_SEQUENTIAL'> = {
  sequential: 'SEQUENTIAL',
  'non-sequential': 'NON_SEQUENTIAL',
  nonsequential: 'NON_SEQUENTIAL',
  'non sequential': 'NON_SEQUENTIAL',
};

const vintageConfigLabel: Record<string, string> = {
  SEQUENTIAL: 'Sequential', NON_SEQUENTIAL: 'Non-Sequential',
};

// ── WineName Config ───────────────────────────────────────────────────────────

export const wineNameConfig: ImportExportConfig = {
  moduleName: 'Wine Names',
  columns: [
    { header: 'Producer', example: 'Gaja' },
    { header: 'Region', example: 'Barbaresco' },
    { header: 'Label Grapes', example: 'Nebbiolo,Barbera' },
    { header: 'Special Name', example: 'Costa Russi' },
    { header: 'Color', example: 'Red' },
    { header: 'Type', example: 'Wine' },
    { header: 'Sub Type', example: 'Still' },
    { header: 'Designation', example: 'DOC' },
    { header: 'Classification', example: 'Grand Cru' },
    { header: 'Vintage Config', example: 'Sequential' },
    { header: 'First Vintage', example: '2005' },
    { header: 'Final Vintage', example: '' },
    { header: 'LWIN ID', example: '' },
    { header: 'Active', example: 'true' },
  ],
  exportFilters: [
    { key: 'search', label: 'Search', type: 'text' },
    {
      key: 'active',
      label: 'Status',
      type: 'select',
      options: [
        { value: '', label: 'All' },
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ],

  importRow: async (row, cache) => {
    const producerName = row['Producer']?.trim();
    if (!producerName) return { action: 'error', displayName: '(empty row)', reason: 'Producer is required', field: 'Producer' };

    const regionName = row['Region']?.trim();
    if (!regionName) return { action: 'error', displayName: producerName, reason: 'Region is required', field: 'Region' };

    const colorInput = (row['Color'] || '').trim().toLowerCase();
    const color = wineColorFromLabel[colorInput];
    if (!color) return {
      action: 'error' as const,
      displayName: producerName,
      field: 'Color',
      reason: `Color "${row['Color'] || ''}" is invalid — expected: Red, White, Rosé, Orange, Sparkling, Dessert, Fortified`,
    };

    // Infer grape color from wine color so auto-created grape stubs are not all RED.
    // Sparkling/Dessert/Fortified are ambiguous — fall back to RED.
    const inferredGrapeColor: 'RED' | 'WHITE' | 'PINK_ROSE' | undefined =
      color === 'RED'                    ? 'RED'      :
      color === 'WHITE' || color === 'ORANGE' ? 'WHITE'   :
      color === 'ROSE'                   ? 'PINK_ROSE':
      undefined;

    const producerId = await cache.findOrCreateProducer(producerName);
    const regionId = await cache.findOrCreateRegion(regionName);

    const grapesRaw = (row['Label Grapes'] || '')
      .split(',').map((g) => g.trim()).filter(Boolean).slice(0, 3);
    const labelGrapeIds: string[] = [];
    for (const g of grapesRaw) {
      try {
        labelGrapeIds.push(await cache.findOrCreateGrape(g, inferredGrapeColor));
      } catch (grapeErr: any) {
        // Rethrow with grape name so the error panel names the failing grape.
        const msg = grapeErr?.response?.data?.message ?? grapeErr?.message ?? 'Unknown error';
        throw new Error(`Grape "${g}": ${msg}`);
      }
    }

    // Sort grape IDs before the duplicate check so that re-imports with grapes
    // in a different column order do not bypass duplicate detection.
    // The backend must treat labelGrapeIds as a set; sorting here ensures we always
    // send them in a consistent canonical order regardless of input order.
    const sortedGrapeIds = [...labelGrapeIds].sort();

    const specialName = row['Special Name']?.trim() || undefined;

    const typeInput = (row['Type'] || '').trim().toLowerCase();
    const wineType = wineTypeFromLabel[typeInput] ?? 'WINE';

    const subTypeInput = (row['Sub Type'] || '').trim().toLowerCase();
    const subType = wineSubTypeFromLabel[subTypeInput] || undefined;

    const designation = row['Designation']?.trim() || undefined;
    const classification = row['Classification']?.trim() || undefined;

    const vintageConfigInput = (row['Vintage Config'] || '').trim().toLowerCase();
    const vintageConfig = vintageConfigFromLabel[vintageConfigInput] || undefined;

    const firstVintageRaw = row['First Vintage']?.trim();
    const firstVintage = firstVintageRaw ? parseInt(firstVintageRaw, 10) : undefined;

    const finalVintageRaw = row['Final Vintage']?.trim();
    const finalVintage = finalVintageRaw ? parseInt(finalVintageRaw, 10) : undefined;

    const lwinId = row['LWIN ID']?.trim() || undefined;
    const active = (row['Active'] || 'true').toLowerCase() !== 'false';

    // Duplicate check (ID-based — the only deterministic check in the system)
    const dupCheck = await wineNameService.checkDuplicate({
      producerId,
      regionId,
      specialName,
      labelGrapeIds: sortedGrapeIds,
    });
    if (dupCheck.isDuplicate) {
      return { action: 'skipped', displayName: producerName, reason: 'Duplicate' };
    }

    await wineNameService.create({
      producerId, regionId,
      labelGrapeIds: sortedGrapeIds,
      specialName,
      color, type: wineType, subType, designation, classification,
      vintageConfig, firstVintage, finalVintage, lwinId, active,
    } as any);

    return { action: 'created', displayName: producerName };
  },

  fetchForExport: async (filters) => {
    const params: Record<string, any> = {};
    if (filters.search) params.search = filters.search;
    if (filters.active !== undefined && filters.active !== '')
      params.active = filters.active === 'true';

    const all = await fetchAllPages((p, ps) =>
      wineNameService.getAll({ ...params, page: p, pageSize: ps })
    );

    return all.map((wn) => ({
      Producer: wn.producer?.fullName || wn.producer?.name || '',
      Region: wn.region?.name || '',
      'Label Grapes': (wn.labelGrapes || []).map((g) => g.primaryName).join(','),
      'Special Name': wn.specialName || '',
      Color: wineColorLabel[wn.color ?? ''] ?? '',
      Type: wineTypeLabel[wn.type ?? ''] ?? '',
      'Sub Type': wineSubTypeLabel[wn.subType ?? ''] ?? '',
      Designation: wn.designation || '',
      Classification: wn.classification || '',
      'Vintage Config': vintageConfigLabel[wn.vintageConfig ?? ''] ?? '',
      'First Vintage': wn.firstVintage != null ? String(wn.firstVintage) : '',
      'Final Vintage': wn.finalVintage != null ? String(wn.finalVintage) : '',
      'LWIN ID': wn.lwinId || '',
      Active: String(wn.active),
    }));
  },
};

// ── Wine Config ───────────────────────────────────────────────────────────────

export const wineConfig: ImportExportConfig = {
  moduleName: 'Wines',
  columns: [
    { header: 'Wine Name', example: 'Gaja Barbaresco Costa Russi' },
    { header: 'Vintage', example: '2018' },
    { header: 'Active', example: 'true' },
  ],
  exportFilters: [
    { key: 'search', label: 'Search', type: 'text' },
    {
      key: 'active',
      label: 'Status',
      type: 'select',
      options: [
        { value: '', label: 'All' },
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
    {
      key: 'vintageType',
      label: 'Vintage Type',
      type: 'select',
      options: [
        { value: '', label: 'All' },
        { value: 'single_vintage', label: 'Single Vintage' },
        { value: 'non_vintage', label: 'Non Vintage (NV)' },
        { value: 'multi_vintage', label: 'Multi Vintage (MV)' },
      ],
    },
  ],

  importRow: async (row) => {
    // Normalize to collapse invisible Unicode whitespace (non-breaking spaces, zero-width
    // chars, etc.) that Excel commonly inserts when copy-pasting from web pages.
    const normalizeQuery = (s: string) =>
      s.replace(/[\u00A0\u200B\uFEFF\t\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();

    const wineNameQuery = normalizeQuery(row['Wine Name'] ?? '');
    if (!wineNameQuery) return { action: 'error', displayName: '(empty row)', reason: 'Wine Name is required', field: 'Wine Name' };

    const vintageRaw = row['Vintage']?.trim();
    if (!vintageRaw) return { action: 'error', displayName: wineNameQuery, reason: 'Vintage is required', field: 'Vintage' };

    const parsed = parseVintageInput(vintageRaw);
    if (!parsed.valid) {
      return {
        action: 'error' as const,
        displayName: wineNameQuery,
        field: 'Vintage',
        reason: parsed.error ?? 'Invalid vintage format',
      };
    }

    // Find the wine name by display name — normalize both sides so invisible
    // Unicode whitespace differences between Excel and the stored displayName don't
    // cause false mismatches.
    const wineNames = await wineService.searchWineNames(wineNameQuery);
    const found = wineNames.find(
      (wn) => normalizeQuery(wn.displayName).toLowerCase() === wineNameQuery.toLowerCase()
    );
    if (!found) {
      return {
        action: 'error' as const,
        displayName: wineNameQuery,
        field: 'Wine Name',
        reason: `Wine name "${wineNameQuery}" not found — create it first in Wine Names`,
      };
    }

    // Duplicate check (ID-based — deterministic)
    const dupCheck = await wineService.checkDuplicate({
      wineNameId: found._id,
      vintageType: parsed.vintageType,
      vintage: parsed.vintage,
    });
    if (dupCheck.isDuplicate) {
      return { action: 'skipped', displayName: wineNameQuery, reason: 'Duplicate' };
    }

    const active = (row['Active'] || 'true').toLowerCase() !== 'false';

    await wineService.create({
      wineNameId: found._id,
      vintageType: parsed.vintageType,
      vintage: parsed.vintage,
      baseVintage: parsed.baseVintage,
      active,
    });

    return { action: 'created', displayName: `${found.displayName} ${parsed.displayVintage}` };
  },

  fetchForExport: async (filters) => {
    const params: Record<string, any> = {};
    if (filters.search) params.search = filters.search;
    if (filters.active !== undefined && filters.active !== '')
      params.active = filters.active === 'true';
    if (filters.vintageType) params.vintageType = filters.vintageType;

    const all = await fetchAllPages((p, ps) =>
      wineService.getAll({ ...params, page: p, pageSize: ps })
    );

    return all.map((w) => ({
      'Wine Name': w.wineName?.displayName || '',
      Vintage: w.displayVintage,
      Active: String(w.active),
    }));
  },
};
